<template>
  <section class="inventory-group-picker">
    <div class="location-row">
      <new-location-picker
        class="location-picker"
        :disabled="isDisabled || isLocationDisabled"
        data-test="pickup-location-selector"
        :label="locationPickerLabel"
        :show-add-location="canCreateLocationOnTheFly"
        :placeholder="locationPickerPlaceholder"
        :value="modelValue.locationId ?? undefined"
        :initial-location-name="modelValue.locationName"
        :can-show-all-locations="canShowAllLocationsInLocationPicker"
        :required="locationRequired"
        @input="updatePickupLocation"
      />
      <vs-button
        v-if="allowRemoveInventoryGroup && !isDisabled"
        data-test="delete-inventory-group"
        icon="clear"
        small
        destructive
        type="tertiary"
        @click="$emit('remove-group')"
      />
    </div>

    <div
      v-for="(item, index) in modelValue.inventory"
      :key="item._uniqueId"
      class="inventory-item-row"
    >
      <job-inventory-and-quantity-picker
        ref="inventoryItemPickerRefs"
        class="inventory-item-picker"
        :model-value="item"
        :resource-type-activity-id="resourceTypeActivityId"
        :disable-inventory-sub-types="getDisabledInventorySubTypes(index)"
        :is-disabled="isDisabled"
        :allow-remove-inventory="allowRemoveInventoryItem && modelValue.inventory.length > 1"
        :allow-zero-inventory="allowZeroInventory"
        @update:modelValue="updateInventoryItem(index, $event)"
        @remove-inventory-item="removeInventoryItem(index)"
      />
    </div>

    <div v-if="allowAddInventoryItem || showAddInventoryGroupButton" class="add-item-row">
      <vs-text class="add-text">Add:</vs-text>
      <vs-button
        v-if="allowAddInventoryItem"
        type="tertiary"
        small
        icon="mdi-cart-plus"
        label="Inventory item"
        :disabled="isDisabled"
        :data-test="`add-inventory-item-${props.modelValue._uniqueId}`"
        @click="addInventoryItem"
      />
      <div v-if="allowAddInventoryItem && showAddInventoryGroupButton" class="vertical-divider" />

      <v-tooltip
        v-if="showAddInventoryGroupButton"
        :disabled="!disableAddInventoryGroupButton"
        top
        max-width="400px"
      >
        <template #activator="{ on, attrs }">
          <span v-bind="attrs" v-on="on">
            <vs-button
              :disabled="disableAddInventoryGroupButton"
              type="tertiary"
              small
              icon="mdi-map-marker-plus"
              label="Pickup location"
              :data-test="`add-group-${props.modelValue._uniqueId}`"
              @click="$emit('add-group')"
            />
          </span>
        </template>
        <span>Select a resource type and activity to add inventory</span>
      </v-tooltip>
    </div>
  </section>
</template>

<script setup lang="ts">
import { computed, toRef } from 'vue'
import VsButton from '@/components/vision/VsButton.vue'
import VsText from '@/components/vision/VsText.vue'
import JobInventoryAndQuantityPicker from './JobInventoryAndQuantityPicker.vue'
import NewLocationPicker from '@/components/locations/NewLocationPicker.vue'
import { useBusinessUnitPermissions } from '@/components/tickets/common/useBusinessUnitPermissions'
import type { InventoryPickerGroup } from './types'
import { useJobInventoryGroupPicker } from './JobInventoryGroupPicker'
import { JobStopType } from '@ironsight/types'

interface InventoryGroupPickerProps {
  modelValue: InventoryPickerGroup
  resourceTypeActivityId: number
  isLocationDisabled?: boolean // Disable location picker
  allowEmptyPickupLocation?: boolean
  isDisabled?: boolean // Disable all pickers
  showAddInventoryGroupButton?: boolean
  allowRemoveInventoryGroup?: boolean
  allowAddInventoryItem?: boolean
  allowRemoveInventoryItem?: boolean
  hasPickup?: boolean
  allowZeroInventory?: boolean
}

const props = withDefaults(defineProps<InventoryGroupPickerProps>(), {
  isLocationDisabled: false,
  allowEmptyPickupLocation: true,
  isDisabled: false,
  allowRemoveInventoryGroup: true,
  allowRemoveInventoryItem: true,
  allowAddInventoryItem: true,
  showAddInventoryGroupButton: true,
  allowZeroInventory: false,
})

const disableAddInventoryGroupButton = computed(
  () => !props.resourceTypeActivityId || props.resourceTypeActivityId === -1,
)

const { canShowAllLocationsInLocationPicker, canCreateLocationOnTheFly } =
  useBusinessUnitPermissions()

const emit = defineEmits<{
  (e: 'update:modelValue', value: InventoryPickerGroup): void
  (e: 'remove-group'): void
  (e: 'add-group'): void
}>()

const modelValueRef = toRef(props, 'modelValue')

const {
  inventoryItemPickerRefs,
  getDisabledInventorySubTypes,
  addInventoryItem,
  removeInventoryItem,
  updateInventoryItem,
} = useJobInventoryGroupPicker(modelValueRef, emit)

function stopTypeDisplayString(stopType: JobStopType) {
  switch (stopType) {
    case JobStopType.DropOff:
      if (props.hasPickup) {
        return 'Dropoff'
      }
      return 'Destination'
    case JobStopType.PickUp:
      return 'Pickup'
  }
}

const locationPickerLabel = computed(
  () => `${stopTypeDisplayString(props.modelValue.stopType)} location`,
)

const locationRequired = computed(
  () => !props.allowEmptyPickupLocation || modelValueRef.value.stopType === JobStopType.DropOff,
)

const locationPickerPlaceholder = computed(() => {
  const displayStringLowerCase = stopTypeDisplayString(props.modelValue.stopType).toLowerCase()
  return locationRequired.value
    ? `Select a ${displayStringLowerCase} location`
    : `Let the dispatcher decide ${displayStringLowerCase} location`
})

const updatePickupLocation = (id?: number | number[]) => {
  const locationId = Array.isArray(id) ? id[0] : id
  if (locationId === props.modelValue.locationId) return
  const updatedGroup: InventoryPickerGroup = {
    ...props.modelValue,
    locationId,
  }
  emit('update:modelValue', updatedGroup)
}
</script>

<style scoped>
.inventory-group-picker {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.location-row {
  display: flex;
  align-items: center;
  gap: 0.75rem;
}

.location-picker {
  flex: 1;
  margin-left: 0.25rem;
}

.inventory-item-row {
  display: flex;
  align-items: flex-start;
  gap: 0.5rem;
}

.inventory-item-picker {
  flex: 1;
}

.add-item-row {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-left: 0.25rem;
}

.add-text {
  font-family: Montserrat, sans-serif;
  font-weight: bold;
  color: var(--color-grey--light);
}

.vertical-divider {
  width: 0.05rem;
  height: 1.5rem;
  background-color: #c0c0c0;
  flex-shrink: 0;
}
</style>
